export interface StoreMediaRequirementRow {
  id: number;
  storeId: number;
  mediaFormatId: number;
  quantity: number;
  isActive?: boolean | null;
  updatedAt?: string | Date | null;
  createdAt?: string | Date | null;
}

export interface StoreFormatAssignmentRow {
  storeId: number;
  mediaFormatId: number;
}

export function isActiveStoreMediaRequirement(requirement: StoreMediaRequirementRow): boolean {
  return requirement.quantity > 0 && requirement.isActive !== false;
}

export function listAssignedStoreMediaFormats<T extends StoreFormatAssignmentRow>(
  assignments: T[],
  requirements: StoreMediaRequirementRow[],
): T[] {
  return assignments.filter((assignment) => {
    const requirement = requirements.find(
      (row) => row.mediaFormatId === assignment.mediaFormatId && row.storeId === assignment.storeId,
    );
    return requirement != null && isActiveStoreMediaRequirement(requirement);
  });
}

export function listMediaFormatsAvailableForStore<T extends { id: number }>(
  formats: T[],
  assignments: StoreFormatAssignmentRow[],
  requirements: StoreMediaRequirementRow[],
): T[] {
  const blocked = new Set(
    listAssignedStoreMediaFormats(assignments, requirements).map((row) => row.mediaFormatId),
  );
  return formats.filter((format) => !blocked.has(format.id));
}

/** An explicit bulk assign should replace a retained operator when the format is not live at the store. */
export function shouldSendOperatorAssignment(
  existingAssignment: StoreFormatAssignmentRow | undefined,
  requirement: StoreMediaRequirementRow | undefined,
): boolean {
  if (!existingAssignment) return true;
  return requirement == null || !isActiveStoreMediaRequirement(requirement);
}

export function listUnassignedStoreMediaFormats(
  requirements: StoreMediaRequirementRow[],
  assignments: StoreFormatAssignmentRow[],
): StoreMediaRequirementRow[] {
  const assignedKeys = new Set(
    assignments.map((assignment) => `${assignment.storeId}:${assignment.mediaFormatId}`),
  );

  return requirements.filter((requirement) => (
    isActiveStoreMediaRequirement(requirement)
    && !assignedKeys.has(`${requirement.storeId}:${requirement.mediaFormatId}`)
  ));
}
